/**
 * Toggle Primitive
 * Switch/toggle component with validation states and design system tokens
 */

import React from "react";
import {
  Switch as ChakraSwitch,
  FormControl,
  FormErrorMessage,
  FormHelperText,
} from "@chakra-ui/react";
import { resolveSpacing } from "../foundation";
import { Color } from "../foundation/Color";
import { Spacing } from "../foundation/Spacing";
import { useColorModeValue } from "../hooks/useColorModeValue";

// Branded types for toggle tokens
declare const __toggle_size_brand: unique symbol;
declare const __toggle_state_brand: unique symbol;

export interface ToggleSizeToken {
  readonly [__toggle_size_brand]: true;
  readonly _value: string;
}

export interface ToggleStateToken {
  readonly [__toggle_state_brand]: true;
  readonly _value: string;
}

// Factory functions
const createToggleSizeToken = (value: string): ToggleSizeToken => ({ _value: value } as ToggleSizeToken);
const createToggleStateToken = (value: string): ToggleStateToken => ({ _value: value } as ToggleStateToken);

export interface ToggleProps {
  label?: string;
  helperText?: string;
  errorText?: string;
  size?: ToggleSizeToken;
  state?: ToggleStateToken;
  isChecked?: boolean;
  defaultChecked?: boolean;
  isDisabled?: boolean;
  isRequired?: boolean;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  identifier?: string;
  value?: string;
  id?: string;
  "data-testid"?: string;
}

function ToggleComponent({
  label,
  helperText,
  errorText,
  size,
  state,
  isChecked,
  defaultChecked,
  isDisabled = false,
  isRequired = false,
  onChange,
  onBlur,
  onFocus,
  identifier,
  value,
  id,
  "data-testid": testId,
}: ToggleProps) {
  // Set defaults inside the component to avoid circular reference issues
  const resolvedSize = size || toggleTokens.size.medium;
  const resolvedState = state || toggleTokens.state.default;

  // Determine validation state
  const isInvalid = resolvedState._value === "error" || !!errorText;
  const isSuccess = resolvedState._value === "success";
  const isWarning = resolvedState._value === "warning";

  // Get state colors
  const getStateColors = () => {
    if (isInvalid) {
      return {
        colorScheme: "red",
      };
    } else if (isSuccess) {
      return {
        colorScheme: "green",
      };
    } else if (isWarning) {
      return {
        colorScheme: "orange",
      };
    }

    return {
      colorScheme: "blue",
    };
  };

  const getSizeProps = (size: ToggleSizeToken) => {
    switch (size._value) {
      case "small":
        return {
          size: "sm" as const,
          fontSize: "sm",
        };
      case "large":
        return {
          size: "lg" as const,
          fontSize: "md",
        };
      case "medium":
      default:
        return {
          size: "md" as const,
          fontSize: "sm",
        };
    }
  };

  const stateColors = getStateColors();
  const sizeProps = getSizeProps(resolvedSize);

  return (
    <FormControl isInvalid={isInvalid} isRequired={isRequired}>
      <ChakraSwitch
        {...sizeProps}
        {...stateColors}
        isChecked={isChecked}
        defaultChecked={defaultChecked}
        isDisabled={isDisabled}
        isRequired={isRequired}
        onChange={onChange}
        onBlur={onBlur}
        onFocus={onFocus}
        name={identifier}
        value={value}
        id={id}
        data-testid={testId}
        _focus={{
          boxShadow: `0 0 0 2px ${useColorModeValue(Color.interactive.primary)}`,
        }}
        _disabled={{
          opacity: 0.6,
          cursor: "not-allowed",
        }}
      >
        {label}
      </ChakraSwitch>

      {errorText && (
        <FormErrorMessage
          fontSize="xs"
          color={useColorModeValue(Color.text.error)}
          mt={resolveSpacing(Spacing.tiny)}
        >
          {errorText}
        </FormErrorMessage>
      )}

      {helperText && !errorText && (
        <FormHelperText
          fontSize="xs"
          color={useColorModeValue(Color.text.secondary)}
          mt={resolveSpacing(Spacing.tiny)}
        >
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
}

// Toggle tokens
const toggleTokens = {
  size: {
    small: createToggleSizeToken("small"),
    medium: createToggleSizeToken("medium"),
    large: createToggleSizeToken("large"),
  } as const,

  state: {
    default: createToggleStateToken("default"),
    success: createToggleStateToken("success"),
    warning: createToggleStateToken("warning"),
    error: createToggleStateToken("error"),
  } as const,
};

// Public API - combines tokens and component
export const Toggle = Object.assign(ToggleComponent, toggleTokens);
